import { useState, useEffect } from 'react';

export default function Hero() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAppModal, setShowAppModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen bg-gradient-to-br from-[#0A1628] via-[#0F1B35] to-[#1A0B2E] overflow-hidden">
      {/* Geometric Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#00A8E8] rounded-full opacity-10 blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-[#E91E63] rounded-full opacity-10 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-[#8B5CF6] rounded-full opacity-8 blur-2xl" />
      </div>

      {/* Navbar */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="/img/logo.png"
              alt="VitaUni"
              className="h-12 w-auto"
            />
            
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('como-funciona')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#00A8E8]' : 'text-white hover:text-[#00A8E8]'}`}>
                Como Funciona
              </button>
              <button onClick={() => scrollToSection('beneficios')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#00A8E8]' : 'text-white hover:text-[#00A8E8]'}`}>
                Benefícios
              </button>
              <button onClick={() => scrollToSection('parceiros')} className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-[#00A8E8]' : 'text-white hover:text-[#00A8E8]'}`}>
                Parceiros
              </button>
            </div>

            <button onClick={() => setShowAppModal(true)} className="px-6 py-3 bg-gradient-to-r from-[#00A8E8] to-[#E91E63] text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-[#00A8E8]/50 transition-all whitespace-nowrap cursor-pointer">
              Baixar App
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20 min-h-screen flex items-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center w-full">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm">
              <span className="text-sm text-white/90 whitespace-nowrap">Economia Inteligente em Saúde</span>
            </div>

            <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight">
              Saúde com preço{' '}
              <span className="bg-gradient-to-r from-[#00A8E8] to-[#E91E63] bg-clip-text text-transparent">
                inteligente.
              </span>
            </h1>

            <p className="text-xl text-gray-300 leading-relaxed max-w-xl">
              Descontos reais em consultas, exames e procedimentos. Cuide de você sem pesar no bolso.
            </p>

            <p className="text-base text-white/70 max-w-xl">
              Assinatura e ativação do plano são feitas pelo aplicativo.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => setShowAppModal(true)} className="px-8 py-4 bg-gradient-to-r from-[#00A8E8] to-[#E91E63] text-white font-semibold rounded-full hover:shadow-2xl hover:shadow-[#00A8E8]/50 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer group">
                Baixar o app e começar
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
              </button>
              <button onClick={() => scrollToSection('como-funciona')} className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-[#0A1628] transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer">
                <i className="ri-play-circle-line text-xl"></i>
                Ver Como Funciona
              </button>
            </div>
          </div>

          {/* Right Content - Card Mockup */}
          <div className="relative">
            <div className="relative z-10 transform hover:scale-105 transition-transform duration-500">
              <div className="relative w-full max-w-md mx-auto">
                {/* Card */}
                <div className="relative aspect-[1.586/1] rounded-3xl bg-gradient-to-br from-black via-[#00A8E8] to-[#E91E63] p-8 shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent rounded-3xl" />
                  <div className="relative h-full flex flex-col justify-between">
                    <div>
                      <img 
                        src="/img/logo.png"
                        alt="VitaUni"
                        className="h-10 w-auto"
                      />
                    </div>
                    <div>
                      <div className="text-white/80 text-sm mb-2 whitespace-nowrap">Titular</div>
                      <div className="text-white text-xl font-bold mb-6 whitespace-nowrap">MARIA SILVA SANTOS</div>
                      <div className="flex justify-between items-end">
                        <div>
                          <div className="text-white/80 text-xs mb-1 whitespace-nowrap">Válido até</div>
                          <div className="text-white font-semibold whitespace-nowrap">12/2025</div>
                        </div>
                        <div className="text-white/80 text-xs whitespace-nowrap">**** 4829</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 bg-white rounded-2xl px-6 py-4 shadow-xl animate-bounce" style={{ animationDuration: '3s' }}>
                  <div className="text-3xl font-bold bg-gradient-to-r from-[#00A8E8] to-[#E91E63] bg-clip-text text-transparent whitespace-nowrap">
                    Até 70%
                  </div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">de desconto</div>
                </div>

                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl px-6 py-4 shadow-xl">
                  <div className="text-2xl font-bold text-gray-800 whitespace-nowrap">3.000+</div>
                  <div className="text-sm text-gray-600 whitespace-nowrap">Parceiros</div>
                </div>
              </div>
            </div>

            {/* Glow Effects */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-to-r from-[#00A8E8]/30 to-[#E91E63]/30 blur-3xl -z-10" />
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-3xl text-white/50"></i>
      </div>

      {/* App Download Modal */}
      {showAppModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-6" onClick={() => setShowAppModal(false)}>
          <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-800 mb-3">Baixar o app</h3>
              <p className="text-gray-600">Escolha sua plataforma e comece a economizar</p>
            </div>

            <div className="space-y-4">
              <a 
                href="https://play.google.com/store/apps/details?id=br.com.vitauni&hl=pt_BR" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-black text-white rounded-full hover:bg-gray-800 transition-all cursor-pointer"
              >
                <i className="ri-google-play-fill text-2xl"></i>
                <span className="font-semibold whitespace-nowrap">Disponível na Google Play</span>
              </a>

              <a 
                href="https://apps.apple.com/br/app/vitauni/id6757611955" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-black text-white rounded-full hover:bg-gray-800 transition-all cursor-pointer"
              >
                <i className="ri-apple-fill text-2xl"></i>
                <span className="font-semibold whitespace-nowrap">Disponível na App Store</span>
              </a>
            </div>

            <button 
              onClick={() => setShowAppModal(false)}
              className="mt-6 w-full px-6 py-3 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              Fechar
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
